export type AgentEventType =
  | "answer_submitted_correct"
  | "answer_submitted_incorrect"
  | "word_hovered"
  | "activity_completed"
  | "stall"
  | "manual_help_requested";

import type { AgentAction, AgentActionStatus } from "@lib/agent/actionToken";
import type { AgentLessonContent } from "@lib/agent/lessonSnapshot";

export type LessonPhase = "vocabulary" | "grammar" | "reading" | "speaking" | "quiz";

/** Serializable lesson payload for agent context (matches Prisma Lesson + optional unit/chapter). */
export type AgentLesson = {
  id: number;
  unitId: number;
  slug: string;
  name: string;
  titles: unknown;
  lessonType: string;
  orderIndex: number;
  createdAt?: string | Date;
  unit?: {
    id: number;
    chapterId: number;
    slug: string;
    name: string;
    titles: unknown;
    orderIndex?: number;
    createdAt?: string | Date;
    chapter?: {
      id: number;
      subjectId: number;
      slug: string;
      name: string;
      titles: unknown;
      orderIndex?: number;
      createdAt?: string | Date;
    };
  };
};

export function lessonTypeToPhase(lessonType: string): LessonPhase {
  if (lessonType === "SPEAKING") return "speaking";
  if (lessonType === "READING") return "reading";
  return "grammar";
}

export type AgentEvent = {
  type: AgentEventType;
  payload: string;
  relativeMs: number;
};

/** Client-side lesson binding before runtime fields are added. */
export type LessonContextInput = {
  lesson: AgentLesson;
  content?: AgentLessonContent | null;
  phase?: LessonPhase;
  targetLocale?: string;
  nativeLocale?: string;
};

export type LessonContext = {
  lesson: AgentLesson;
  content: AgentLessonContent | null;
  lessonId: string;
  lessonName: string;
  phase: LessonPhase;
  recentEvents: AgentEvent[];
  performance: { correct: number; incorrect: number; streak: number };
  targetLocale?: string;
  nativeLocale?: string;
};

/**
 * The conversation types live with the conversation code; they were duplicated
 * here, and ConversationPersona had already drifted - the copy was missing
 * curriculumLevelHint.
 */
export type {
  ConversationDebrief,
  ConversationDebriefNewWordItem,
  ConversationDebriefToReviewItem,
  ConversationDifficulty,
  ConversationPersona,
  ConversationTranscriptRow,
} from "@lib/agent/conversation/types";

export type OverlayMode = { type: "tutor" };

export type OverlayMessage = {
  id: string;
  text: string;
  isStreaming: boolean;
  role: "user" | "tutor" | "persona";
  /** Board action proposed by the tutor via a trailing [ACTION{...}] token (D-13). */
  action?: AgentAction;
  /**
   * What the learner did with `action` (CON-123). Owned here rather than in the
   * chip so it survives a reload and reaches the model; absent means untouched.
   */
  actionStatus?: AgentActionStatus;
};

export type OverlayState = {
  mode: OverlayMode;
  messages: OverlayMessage[];
  isOpen: boolean;
  /** Monotonic timestamp bumped when the collapsed widget should spring-spin (stall nudge). */
  nudgeAt: number;
};
